import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useAgentWorkflowStore, Agent } from "@/store/agentsStore";
import { Button } from "@/components/ui/button";
import { Bot, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import Layout from "@/components/layout";

export default function AllAgents() {
  const navigate = useNavigate();
  const { agentMap, agentIds, isLoadingList, fetchAgents, updateAgent } =
    useAgentWorkflowStore();
  const agents = agentIds
    .map((id) => agentMap.get(id))
    .filter(Boolean) as Agent[];

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleAgentClick = (agentId: string) => {
    navigate(`/agents/${agentId}`);
  };

  const handleToggleStatus = async (agentId: string, currentStatus: string) => {
    const newStatus = currentStatus === "ACTIVE" ? "DISABLED" : "ACTIVE";
    await updateAgent(agentId, { status: newStatus });
  };

  if (isLoadingList && agents.length === 0) {
    return (
      <Layout breadcrumbs={[{ label: "Agents", current: true }]}>
        <div className="flex-1 p-6">
          <div className="mb-6">
            <Skeleton className="h-8 w-32 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[{ label: "Agents", current: true }]}>
      <div className="flex-1 p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">All Agents</h1>
          <p className="text-muted-foreground">
            Manage all your AI agents and workflows
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="group relative bg-background border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleAgentClick(agent.id)}
            >
              <div className="absolute top-2 right-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAgentClick(agent.id);
                      }}
                    >
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStatus(agent.id, agent.status);
                      }}
                    >
                      {agent.status === "ACTIVE" ? "Disable" : "Enable"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Bot className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate pr-8">{agent.name}</h3>
                  {agent.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {agent.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        agent.status === "ACTIVE"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {agent.status}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Updated {new Date(agent.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
